<template>
  <Teleport to="body">
    <!-- Overlay (clic = ferme) -->
    <div
      class="fixed inset-0 bg-black/30 backdrop-blur-[1px] z-40"
      @click="$emit('close')"
    />

    <!-- Side panel -->
    <aside
      class="fixed right-0 top-0 bottom-0 w-full sm:w-[520px] lg:w-[620px] bg-white shadow-2xl z-50 flex flex-col"
    >
      <!-- Header -->
      <div :class="['border-b border-dashboard-border p-5 flex items-start justify-between gap-3 border-l-4', SEVERITY_META[log.severity].ring]">
        <div class="flex items-start gap-3 min-w-0">
          <div :class="['p-2 rounded-lg flex-shrink-0', SEVERITY_META[log.severity].badge]">
            <component :is="SEVERITY_META[log.severity].icon" class="w-5 h-5" />
          </div>
          <div class="min-w-0">
            <h2 class="text-lg font-bold text-dashboard-text">{{ getActionLabel(log.action) }}</h2>
            <p class="text-xs text-dashboard-muted mt-0.5 font-mono">{{ log.action }}</p>
            <p class="text-xs text-dashboard-muted mt-1">{{ formatDateTime(log.created_at) }} · {{ formatRelative(log.created_at) }}</p>
          </div>
        </div>
        <button
          @click="$emit('close')"
          class="p-2 hover:bg-dashboard-bg rounded-lg transition-colors flex-shrink-0"
          title="Fermer"
        >
          <X class="w-5 h-5 text-dashboard-muted" />
        </button>
      </div>

      <!-- Content (scroll) -->
      <div class="flex-1 overflow-y-auto p-5 space-y-5">
        <!-- Section : Qui -->
        <Section title="Qui" :icon="User">
          <template v-if="log.user">
            <KV label="Nom">{{ log.user.full_name }}</KV>
            <KV label="Username">
              <router-link
                :to="`/audit/user/${log.user.id}`"
                class="font-mono text-blue-600 hover:underline"
              >@{{ log.user.username }}</router-link>
            </KV>
            <KV label="ID interne">{{ log.user.id }}</KV>
            <div class="pt-2">
              <router-link
                :to="`/audit/user/${log.user.id}`"
                class="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
              >
                <Activity class="w-3 h-3" /> Voir toute l'activité de cet utilisateur
              </router-link>
            </div>
          </template>
          <p v-else class="text-sm text-dashboard-muted italic">Action système ou utilisateur anonyme (pas de session).</p>
        </Section>

        <!-- Section : Quoi -->
        <Section title="Quoi" :icon="Activity">
          <KV label="Action">
            <span class="text-dashboard-text">{{ getActionLabel(log.action) }}</span>
          </KV>
          <KV label="Domaine">
            <span :class="['inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium', domainMeta.badge]">
              <component :is="domainMeta.icon" class="w-3 h-3" />
              {{ domainMeta.label }}
            </span>
          </KV>
          <KV label="Sévérité">
            <span :class="['inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium', SEVERITY_META[log.severity].badge]">
              <component :is="SEVERITY_META[log.severity].icon" class="w-3 h-3" />
              {{ SEVERITY_META[log.severity].label }}
            </span>
          </KV>
          <KV label="Source">
            <span :class="['inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium', SOURCE_META[log.source].badge]">
              <component :is="SOURCE_META[log.source].icon" class="w-3 h-3" />
              {{ SOURCE_META[log.source].label }}
            </span>
          </KV>
        </Section>

        <!-- Section : Sur quoi (cible) -->
        <Section v-if="hasTarget" title="Sur quoi" :icon="MousePointer2">
          <template v-if="log.courrier_id">
            <KV label="Type">Courrier</KV>
            <KV label="ID">#{{ log.courrier_id }}</KV>
            <div class="pt-2">
              <router-link
                :to="`/audit/courrier/${log.courrier_id}`"
                class="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200"
              >
                <ListTree class="w-3 h-3" /> Voir la timeline de ce courrier
              </router-link>
            </div>
          </template>
          <template v-else-if="log.target_id">
            <KV label="Type">{{ getTargetLabel(log.target_type) }}</KV>
            <KV label="ID">{{ log.target_id }}</KV>
          </template>
        </Section>

        <!-- Section : Quand (technique) -->
        <Section title="Contexte technique" :icon="Server">
          <KV label="Date / Heure">{{ formatDateTime(log.created_at) }}</KV>
          <KV v-if="log.ip_address" label="Adresse IP">
            <span class="font-mono">{{ log.ip_address }}</span>
          </KV>
          <KV v-if="log.session_key" label="Session">
            <span class="font-mono text-xs">{{ truncateMiddle(log.session_key, 16) }}</span>
          </KV>
          <KV v-if="log.request_id" label="Request ID">
            <div class="flex items-center gap-2 min-w-0">
              <code class="font-mono text-xs truncate">{{ log.request_id }}</code>
              <button @click="copy(log.request_id!)" class="text-dashboard-muted hover:text-dashboard-text" title="Copier">
                <Copy class="w-3.5 h-3.5" />
              </button>
              <router-link
                :to="`/audit/request/${log.request_id}`"
                class="text-blue-600 hover:underline text-xs whitespace-nowrap"
                title="Voir toutes les entrées du même request_id"
              >
                Corrélation ›
              </router-link>
            </div>
          </KV>
          <KV v-if="log.user_agent" label="User-Agent">
            <span class="text-xs text-dashboard-muted break-all">{{ log.user_agent }}</span>
          </KV>
        </Section>

        <!-- Section : Diff before/after -->
        <Section v-if="hasDiff" title="Modifications" :icon="ArrowLeftRight">
          <DiffView :before="log.before || {}" :after="log.after || {}" />
        </Section>

        <!-- Section : Détails JSON -->
        <Section v-if="hasDetails" title="Détails complémentaires" :icon="Code">
          <pre class="text-xs bg-dashboard-bg p-3 rounded-lg overflow-x-auto text-dashboard-text">{{ jsonPretty(log.details) }}</pre>
        </Section>
      </div>

      <!-- Footer -->
      <div class="border-t border-dashboard-border p-3 flex items-center justify-between gap-2 bg-dashboard-bg">
        <span class="text-xs text-dashboard-muted">Entrée #{{ log.id }}</span>
        <button
          @click="copy(JSON.stringify(log, null, 2))"
          class="text-xs px-3 py-1.5 rounded-lg border border-dashboard-border hover:bg-white flex items-center gap-1.5"
        >
          <Copy class="w-3 h-3" /> Copier l'entrée (JSON)
        </button>
      </div>
    </aside>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  X, User, Activity, MousePointer2, ListTree, Server, Copy, Code, ArrowLeftRight,
} from 'lucide-vue-next'

import Section from './DetailSection.vue'
import KV from './KV.vue'
import DiffView from './DiffView.vue'
import {
  SEVERITY_META, SOURCE_META, getActionLabel, getDomainMeta, getTargetLabel,
  formatDateTime, formatRelative,
} from '@/utils/auditMeta'
import type { AuditLogEntry } from '@/utils/auditMeta'

const props = defineProps<{ log: AuditLogEntry }>()
defineEmits<{ (e: 'close'): void }>()

const domainMeta = computed(() => getDomainMeta(props.log.action))

const hasTarget = computed(() => Boolean(props.log.courrier_id || props.log.target_id))
const hasDiff = computed(() => {
  const b = props.log.before || {}
  const a = props.log.after || {}
  return Object.keys(b).length > 0 || Object.keys(a).length > 0
})
const hasDetails = computed(() => {
  const d = props.log.details
  return d && Object.keys(d).length > 0
})

function jsonPretty(value: any): string {
  try { return JSON.stringify(value, null, 2) } catch { return String(value) }
}

function truncateMiddle(s: string, total: number): string {
  if (s.length <= total) return s
  const half = Math.floor((total - 1) / 2)
  return `${s.slice(0, half)}…${s.slice(-half)}`
}

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    // no-op (some browsers block in non-secure contexts)
  }
}
</script>
